import {Col, Form, Input, InputNumber, Row, Select} from 'antd';
import React from 'react';

import type {PackagingChecklist} from '../../../services/interface/pcfTypes';
import {LCI_FIELD_HINTS} from '../constants/lciFieldHints';
import {
  PCF_COL_GUTTER_CLASS,
  PCF_COL_SPAN_HALF,
  PCF_COL_SPAN_THIRD,
  rowGutter
} from '../constants/pcfFormLayout';
import {
  CONFIDENCE_LEVELS,
  MATERIAL_SOURCINGS,
  MATERIAL_UNITS,
  PACKAGING_OPTIONS,
  SUPPLIER_EF_OPTIONS
} from '../constants/pcfOptions';
import {useExpertMode} from '../hooks/useExpertMode';
import {A1MaterialsKeys, A1MaterialsLabels} from '../PCFKeyLabels';

import ChoiceChips from './ChoiceChips';
import {FormItemLabel} from './FormItemLabel';
import {
  positiveUnitsRule,
  supplierEfSourceRules,
  supplierEfValueRules
} from './lciValidation';
import PCFAddButton from './PCFAddButton';
import RemovableCard from './RemovableCard';

const BASE = 'a1';

const blankMaterial = () => ({
  name: '',
  qty: '',
  unit: 'kg',
  sourcing: 'india_national',
  supplierEF: 'no_database_default',
  supplierEfValue: '',
  supplierEfSource: '',
  emissionFactor: '',
  confidence: 'bom_system'
});

export const a1MaterialsDefaults = {
  materials: [blankMaterial()],
  packaging: {
    primary: false,
    secondary: false,
    tertiary: false,
    lubricants: false,
    none: false,
    notSure: false
  }
};

function MaterialFields({name}: {name: number}) {
  const form = Form.useFormInstance();
  const expertMode = useExpertMode();
  const supplierEF = Form.useWatch(
    [
      BASE,
      A1MaterialsKeys.MATERIALS,
      name,
      A1MaterialsKeys.SUPPLIER_EMISSION_FACTOR
    ],
    form
  );

  return (
    <Row gutter={rowGutter}>
      <Col className={PCF_COL_GUTTER_CLASS} span={24}>
        <Form.Item
          name={[name, A1MaterialsKeys.MATERIAL_NAME]}
          label={A1MaterialsLabels.MATERIAL_NAME}
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Required'
            }
          ]}
          validateFirst
          hasFeedback
        >
          <Input className="input" placeholder="Type to search library..." />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_THIRD}>
        <Form.Item
          name={[name, A1MaterialsKeys.QUANTITY_PER_UNIT]}
          required
          label={A1MaterialsLabels.QUANTITY_PER_UNIT}
          rules={[positiveUnitsRule(A1MaterialsLabels.QUANTITY_PER_UNIT)]}
          validateFirst
          hasFeedback
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_THIRD}>
        <Form.Item
          name={[name, A1MaterialsKeys.UNIT]}
          label={A1MaterialsLabels.UNIT}
          rules={[{required: true, message: 'Required'}]}
          validateFirst
          hasFeedback
        >
          <Select
            className="select"
            placeholder="Select"
            options={MATERIAL_UNITS}
            style={{width: '100%'}}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_THIRD}>
        <Form.Item
          name={[name, A1MaterialsKeys.MATERIAL_SOURCING]}
          label={A1MaterialsLabels.MATERIAL_SOURCING}
          rules={[{required: true, message: 'Required'}]}
          validateFirst
          hasFeedback
        >
          <Select
            className="select"
            placeholder="Select"
            options={MATERIAL_SOURCINGS}
            style={{width: '100%'}}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, A1MaterialsKeys.SUPPLIER_EMISSION_FACTOR]}
          label={A1MaterialsLabels.SUPPLIER_EMISSION_FACTOR}
          rules={[{required: true, message: 'Required'}]}
          validateFirst
          hasFeedback
        >
          <Select
            className="select"
            placeholder="Select"
            options={SUPPLIER_EF_OPTIONS}
            style={{width: '100%'}}
          />
        </Form.Item>
      </Col>
      {supplierEF === 'yes_enter' && (
        <>
          <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
            <Form.Item
              name={[name, A1MaterialsKeys.SUPPLIER_EF_VALUE]}
              required={supplierEF === 'yes_enter'}
              label={A1MaterialsLabels.SUPPLIER_EF_VALUE}
              rules={supplierEfValueRules(() => supplierEF ?? '')}
              validateFirst
              hasFeedback
            >
              <InputNumber
                controls={false}
                className="input"
                style={{width: '100%'}}
                min={0}
              />
            </Form.Item>
          </Col>
          <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
            <Form.Item
              name={[name, A1MaterialsKeys.SUPPLIER_EF_SOURCE]}
              required={supplierEF === 'yes_enter'}
              label={A1MaterialsLabels.SUPPLIER_EF_SOURCE}
              rules={supplierEfSourceRules(() => supplierEF ?? '')}
              validateFirst
              hasFeedback
            >
              <Input className="input" placeholder="Source / citation" />
            </Form.Item>
          </Col>
        </>
      )}
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, A1MaterialsKeys.EMISSION_FACTOR]}
          label={
            <FormItemLabel hint={LCI_FIELD_HINTS.emissionFactor}>
              {A1MaterialsLabels.EMISSION_FACTOR}
            </FormItemLabel>
          }
        >
          {expertMode ? (
            <InputNumber
              controls={false}
              className="input"
              style={{width: '100%'}}
              min={0}
            />
          ) : (
            <Input
              className="input"
              disabled
              placeholder="Auto from database"
            />
          )}
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, A1MaterialsKeys.CONFIDENCE]}
          label={A1MaterialsLabels.CONFIDENCE}
          rules={[{required: true, message: 'Required'}]}
          validateFirst
          hasFeedback
        >
          <Select
            className="select"
            placeholder="Select"
            options={CONFIDENCE_LEVELS}
            style={{width: '100%'}}
          />
        </Form.Item>
      </Col>
    </Row>
  );
}

const PACKAGING_CHIP_OPTIONS = [
  ...PACKAGING_OPTIONS,
  {value: 'none', label: 'None'},
  {value: 'notSure', label: 'Not sure'}
];

interface PackagingChecklistFieldProps {
  value?: PackagingChecklist;
  onChange?: (value: PackagingChecklist) => void;
}

const PackagingChecklistField: React.FC<PackagingChecklistFieldProps> = ({
  value,
  onChange
}) => {
  const packaging = value ?? a1MaterialsDefaults.packaging;

  const togglePackaging = (key: string) => {
    onChange?.({
      ...packaging,
      [key]: !packaging[key as keyof PackagingChecklist]
    });
  };

  return (
    <ChoiceChips
      options={PACKAGING_CHIP_OPTIONS}
      values={packaging}
      onToggle={togglePackaging}
    />
  );
};

const A1MaterialsSection: React.FC = () => {
  const form = Form.useFormInstance();
  const packaging =
    Form.useWatch([BASE, A1MaterialsKeys.PACKAGING_CHECKLIST], form) ??
    a1MaterialsDefaults.packaging;

  return (
    <div className="lci-section">
      <div className="lci-section-header">
        <h3 className="lci-section-title">A1 · Bill of Materials</h3>
      </div>

      <Form.List
        name={[BASE, A1MaterialsKeys.MATERIALS]}
        rules={[
          {
            validator: async (_, materials) => {
              if (!materials?.length) {
                return await Promise.reject(
                  new Error('Add at least one material')
                );
              }

              await Promise.resolve();
            }
          }
        ]}
      >
        {(fields, {add, remove}, {errors: listErrors}) => (
          <>
            {listErrors?.length > 0 && (
              <div className="field-error">{listErrors[0]}</div>
            )}
            <div className="lci-section-header" style={{marginTop: 0}}>
              <PCFAddButton
                onClick={() => {
                  add(blankMaterial());
                }}
              >
                Add Material
              </PCFAddButton>
            </div>
            <div className="lci-cards-list">
              {fields.map(({key, name}, idx) => (
                <RemovableCard
                  key={key}
                  title={`Material #${idx + 1}`}
                  onRemove={() => {
                    remove(name);
                  }}
                  showRemove={idx > 0}
                >
                  <MaterialFields name={name} />
                </RemovableCard>
              ))}
            </div>
          </>
        )}
      </Form.List>

      <div className="packaging-block">
        <h4 className="packaging-title">
          {A1MaterialsLabels.PACKAGING_CHECKLIST}
        </h4>
        <Form.Item
          name={[BASE, A1MaterialsKeys.PACKAGING_CHECKLIST]}
          noStyle
        >
          <PackagingChecklistField />
        </Form.Item>
        {packaging.notSure && (
          <p className="field-hint">
            Check with your packaging or logistics team. Packaging typically
            contributes 5–15% of a manufactured product&apos;s A1 footprint.
          </p>
        )}
      </div>
    </div>
  );
};

export default A1MaterialsSection;
